/**
 * The mail module (E1): a shared 1:1 mailbox, not a sender.
 *
 * One screen with three panes - folders, conversations, the open conversation -
 * rather than a list page that navigates away to a detail page. The reason is
 * the work itself: triaging a morning's mail means moving through twenty
 * threads, and a full page load between each of them turns a two-minute job
 * into a ten-minute one. The route still carries the open thread
 * (`/email/:id`), so a link to one conversation still opens that conversation.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PenLine } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { hostSiteId, type StaffUser } from "../../../lib/auth";
import { followUpNoticeKey, getCallFollowUp } from "../../../lib/callFollowUp";
import { getBrainEmailDraft } from "../../../lib/siteBrainEmail";
import {
  deleteThread,
  getMailStats,
  getThread,
  getThreads,
  patchThread,
  suggestMailReply,
  syncMail,
  type MailStats,
  type MailThread,
} from "../../../lib/mail";
import { useSplitter, useToast } from "../../ui";
import { PopHost } from "../../shell/Pop";
import { useSignals } from "../../shell/SignalsProvider";
import { useCrmLabels } from "../../useCrmLabels";
import { Composer, type ComposerDraft } from "./Composer";
import { DraftList, DraftPaneEmpty, useDraftsView } from "./drafts";
import { RailScrim, WorkFab, useRailSheet, workView } from "../_shared/workMobile";
import { useWorkDensity } from "../_shared/density";
import { useReadingPane } from "../_shared/readingPane";
import { FolderRail } from "./FolderRail";
import { ImapMessageList, ImapMessagePane, type SentAllRow } from "./imap";
import { MailboxDialog, START_VIEW, rowView, useMailRail, viewMailbox, viewRef, type MailView } from "./rail";
import { useRailPanes } from "./rail/useRailPanes";
import { ThreadList } from "./ThreadList";
import { ThreadPane } from "./ThreadPane";
import type { TemplateSeed } from "./TemplateMenu";
import { syncMessage } from "./syncResult";
import { ConfirmModal } from "../_shared/ConfirmModal";
import { DEFAULT_MAIL_FILTERS, mailFilterQuery, type MailFilters } from "./filters";
import { useCampaignNames } from "./useCampaignNames";
import { useThreadBulk } from "./useThreadBulk";
import { useSendFlow } from "./useSendFlow";
import { useEstimateComposeLink } from "./useEstimateComposeLink";

/** Prefix a subject once. "Re: Re: Re:" is a thread nobody wants to read. */
const prefixed = (s: string, p: string) => (s.toLowerCase().startsWith(p.toLowerCase()) ? s : `${p} ${s}`);

export function EmailSection({ user }: { user: StaffUser }) {
  const t = useT();
  const navigate = useNavigate();
  // Read off the path rather than through a nested <Routes>: a route switch
  // would remount the whole workspace, and the folder, the search box and the
  // splitter width all have to survive moving between conversations.
  const { pathname, search } = useLocation();
  const id = pathname.startsWith("/email/") ? pathname.slice("/email/".length).split("/")[0] || null : null;
  const toast = useToast();
  const rail = useMailRail(toast, t("mail.fav.saveFailed"), t("mail.box.orderFailed"));
  const { refresh } = useSignals();
  const campaigns = useCampaignNames();
  const { labels } = useCrmLabels();

  /**
   * What the rail has open: a mailbox's conversations, its unsent letters, its
   * Sent folder or another server folder, or one of the combined views. A
   * server folder takes both panes over; the thread underneath is left where
   * it was, and so is the conversation list the rail last showed.
   */
  const [mailView, setMailView] = useState<MailView>(START_VIEW);
  const lastThreads = useRef<Extract<MailView, { kind: "threads" }>>(START_VIEW as Extract<MailView, { kind: "threads" }>);
  if (mailView.kind === "threads") lastThreads.current = mailView;
  const listView = lastThreads.current;
  const [campaignId, setCampaignId] = useState<string | null>(null);
  /** The own-mailbox dialog: "new", an id to change, or closed. */
  const [boxDialog, setBoxDialog] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState<MailFilters>(DEFAULT_MAIL_FILTERS);
  const [rows, setRows] = useState<MailThread[]>([]);
  const [stats, setStats] = useState<MailStats | null>(null);
  const [listBusy, setListBusy] = useState(true);
  const [thread, setThread] = useState<MailThread | null>(null);
  const [threadBusy, setThreadBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [draft, setDraft] = useState<(ComposerDraft & { key: number }) | null>(null);
  /**
   * Open the composer on a letter. Every letter gets a window of its own - the
   * key below remounts it - so a draft being continued simply is the window's
   * first state, and the previous letter's draft is saved on its way out
   * rather than overwritten by the next one's.
   */
  const draftSeq = useRef(0);
  const openComposer = useCallback((d: ComposerDraft) => setDraft({ ...d, key: ++draftSeq.current }), []);
  /** A reply asked for from a list row, waiting on that thread to arrive. */
  const [replyWhenLoaded, setReplyWhenLoaded] = useState<string | null>(null);
  /** A row the right-click menu is about to delete. The reading pane asks its
   *  own question before deleting; a menu entry on a row needs one too. */
  const [confirmDel, setConfirmDel] = useState<MailThread | null>(null);
  /** The company whose brain letter is open, so a successful send can be stamped. */
  const brainDomain = useRef<string | null>(null);

  /**
   * `/email?to=someone@example.com` opens the composer already addressed.
   *
   * The register's map and company cards used to offer a bare `mailto:` link,
   * which on a machine with no mail handler registered did nothing whatsoever.
   * This is the destination those buttons actually meant: the CRM's own mailbox,
   * where the message is sent from the company's own address and filed.
   *
   * The parameter is consumed as soon as it is read. Left in the URL, a reload
   * or a step back through history would reopen the composer over whatever the
   * operator had since moved on to.
   */
  useEffect(() => {
    const to = new URLSearchParams(search).get("to");
    if (!to) return;
    openComposer({ mode: "new", to: [to], subject: "", body: "" });
    navigate("/email", { replace: true });
  }, [search, navigate]);

  /**
   * `/email?brain=firma.ee` opens the composer with the development brain's
   * letter already in it.
   *
   * ASYNCHRONOUS, unlike `?to=` above, and that is the whole point of the
   * parameter rather than an implementation detail: the recipient is decided by
   * the server's legal gate on this request, not carried across from the card
   * that linked here. A domain in the URL cannot address a letter; only the
   * answer to this fetch can, and a company that has unsubscribed in the
   * meantime comes back with no address at all.
   *
   * The parameter is consumed only AFTER the fetch settles. Clearing it first
   * would leave a failed load with nothing to retry and no explanation.
   */
  useEffect(() => {
    const domain = new URLSearchParams(search).get("brain");
    if (!domain) return;
    let alive = true;
    // Remembered past the URL, which is cleared as soon as the fetch settles.
    // A ref rather than state on purpose: nothing renders from it, and a state
    // update here would re-run the send handler's closure for no reason.
    brainDomain.current = domain;
    void getBrainEmailDraft(domain)
      .then((d) => {
        if (!alive) return;
        openComposer({
          mode: "new",
          // No fallback to anything on this screen: the gate withheld it, and
          // an empty recipient the operator has to fill in by hand is the
          // correct outcome rather than a inconvenience to work around.
          to: d.to ? [d.to] : [],
          subject: d.subject,
          body: d.body,
        });
        if (!d.to) toast(t("email.brainNoRecipient"));
      })
      .catch(() => {
        if (alive) toast(t("email.brainDraftFailed"));
      })
      .finally(() => {
        if (alive) navigate("/email", { replace: true });
      });
    return () => {
      alive = false;
    };
  }, [search, navigate, toast, t]);

  /**
   * `/email?call=<id>` opens the composer with the letter drafted from a call.
   *
   * The same shape as `?brain=` above and for the same two reasons. The letter
   * itself was written on the call screen, where pressing the button spends the
   * model request; this fetch is free, so a reload or a step back through
   * history cannot cost anything. And the RECIPIENT is decided by this request
   * rather than carried across in the URL - the address is resolved through the
   * records the call is filed against, with the do-not-contact list in front of
   * it, so somebody who has since unsubscribed arrives with no address at all.
   *
   * A 404 here means the button was never pressed, which is not a state the
   * navigation can reach on its own - so it is reported like any other failure
   * rather than given its own wording.
   */
  useEffect(() => {
    const callId = new URLSearchParams(search).get("call");
    if (!callId) return;
    let alive = true;
    void getCallFollowUp(callId)
      .then((d) => {
        if (!alive) return;
        openComposer({ mode: "new", to: d.address ? [d.address] : [], subject: d.subject, body: d.body });
        // Different silences worth different sentences: nobody's address is on
        // file, somebody's address is on file and they said no, or the legal
        // gate forbids writing to the company at all. An operator who cannot
        // tell them apart looks the address up by hand and sends the letter anyway.
        const notice = followUpNoticeKey(d);
        if (notice) toast(t(notice));
      })
      .catch(() => {
        if (alive) toast(t("email.callDraftFailed"));
      })
      .finally(() => {
        if (alive) navigate("/email", { replace: true });
      });
    return () => {
      alive = false;
    };
  }, [search, navigate, toast, t]);

  useEstimateComposeLink(search, navigate, openComposer);

  const [aiBusy, setAiBusy] = useState(false);

  // Where the open letter sits. Read before the splitter is built, because it
  // is what decides which way that splitter's second divider travels.
  const [readingPane, setReadingPane] = useReadingPane();
  const { gridRef, railRef, leftRef, rightRef, railHandleProps, handleProps } = useSplitter({
    minRail: 168,
    minLeft: 300,
    minRight: 380,
    layout: readingPane,
    minTop: 180,
    minBottom: 260,
  });
  const [density, toggleDensity] = useWorkDensity();
  const { sentFolder, imapSource, imapPath, imap, imapActions, sentAll, ownAddress, threadTag, sentTag } =
    useRailPanes(mailView, setMailView, rail);
  const [railSheet, setRail] = useRailSheet(pathname);

  /**
   * Which pane a phone shows. Two list/pane pairs live on this screen and they
   * answer to different things: the CRM's own conversations are opened by the
   * route, a mail server folder's messages by a uid held in state. An open
   * folder takes both panes, so it decides.
   */
  const view = workView(
    imapPath !== null ? (imap.uid === null ? null : String(imap.uid)) : mailView.kind === "sentAll" ? sentAll.openKey : id,
  );

  /** Out of the open item, back to whichever list it came from. */
  const closeImap = imap.close;
  const closeSentAll = sentAll.close;
  const back = useCallback(() => {
    if (imapPath !== null) closeImap();
    else if (mailView.kind === "sentAll") closeSentAll();
    else navigate("/email");
  }, [imapPath, mailView.kind, closeImap, closeSentAll, navigate]);

  /**
   * Open whatever the rail picked.
   *
   * The route goes back to `/email` when the pick is not a conversation list.
   * The thread it named is not on screen any more, and a URL that still claims
   * one is a link that opens something the sender never saw. The thread itself
   * stays in state, so going back to the inbox returns to the conversation
   * rather than to an empty pane.
   */
  const openView = useCallback(
    (next: MailView) => {
      setMailView(next);
      if (next.kind !== "threads" && id) navigate("/email");
    },
    [id, navigate],
  );

  const loadList = useCallback(async () => {
    setListBusy(true);
    try {
      const res = await getThreads({
        status: listView.status,
        mailbox: listView.mailbox,
        ...mailFilterQuery(filters),
        pageSize: 100,
        q: q || undefined,
        campaignId: campaignId ?? undefined,
      });
      setRows(res.rows);
      return res.rows;
    } finally {
      setListBusy(false);
    }
    // `listView` by its pin string: the same list asked twice is one request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewRef(listView), q, campaignId, filters]);

  const bulk = useThreadBulk({ loadList, refresh, thread, setThread, navigate });

  // Debounced, because the search box shares this effect: a folder switch pays
  // 200ms it does not need, which is cheaper than a second code path.
  useEffect(() => {
    const timer = setTimeout(() => void loadList(), 200);
    return () => clearTimeout(timer);
  }, [loadList]);

  const reloadRail = rail.reload;
  const refreshStats = useCallback(() => {
    getMailStats()
      .then(setStats)
      .catch(() => {});
    // The rail's per-mailbox counts move with the same changes.
    void reloadRail();
  }, [reloadRail]);
  useEffect(refreshStats, [refreshStats, rows]);

  const drafts = useDraftsView({
    openComposer,
    navigate,
    refreshStats,
    shown:
      mailView.kind === "drafts"
        ? { kind: mailView.scheduled ? "scheduled" : "drafts", mailbox: mailView.mailbox }
        : null,
    onLeave: () => mailView.kind === "drafts" && setMailView(rowView(mailView.mailbox, "inbox")),
  });
  // The scheduler sends in the background, so the counts beside Drafts and
  // Scheduled go stale on their own; opening one is when they are read.
  useEffect(() => {
    if (mailView.kind === "drafts") refreshStats();
  }, [mailView, refreshStats]);

  // The open thread comes from the route, so a bookmark opens the right one and
  // the browser's back button walks the conversations.
  const loadThread = useCallback((threadId: string) => {
    setThreadBusy(true);
    getThread(threadId)
      .then((th) => setThread(th))
      .catch(() => setThread(null))
      .finally(() => setThreadBusy(false));
  }, []);

  useEffect(() => {
    if (!id) return;
    loadThread(id);
  }, [id, loadThread]);

  // Landing on /email with nothing open shows the folder's first conversation
  // rather than an empty pane. It does NOT mark it read - only a click counts
  // as having read something, so opening the inbox never clears a badge.
  const autoOpened = useRef<string | null>(null);
  useEffect(() => {
    if (id || !rows.length) return;
    const first = rows[0]!.id;
    if (autoOpened.current === first) return;
    autoOpened.current = first;
    loadThread(first);
  }, [id, rows, loadThread]);

  const send = useSendFlow({
    draft,
    thread,
    stats,
    closeComposer: () => setDraft(null),
    loadThread,
    loadList,
    refresh,
    refreshStats,
    scheduleLetter: drafts.scheduleLetter,
    brainDomain,
  });

  const openThread = (threadId: string) => {
    setRows((prev) => prev.map((r) => (r.id === threadId ? { ...r, unread: false } : r)));
    navigate(`/email/${threadId}`);
  };

  /**
   * Patch any thread, whether it is the open one or a row in the list.
   *
   * Caught, because every caller fires this with `void` and nothing awaits it.
   * A staff member with `view:crm` but not `manage:crm.email` may open the inbox
   * and press these buttons; without this the PATCH answered 403 and the screen
   * said nothing at all - the star stayed dark and looked like a click that
   * missed.
   */
  const patchRow = async (id: string, body: Record<string, unknown>) => {
    try {
      const updated = await patchThread(id, body);
      // Only when it is the open one. The list's right-click menu acts on any
      // row, and writing its answer into the reading pane would swap the
      // conversation the operator is reading for one they only filed in passing.
      setThread((prev) => (prev && prev.id === id ? { ...prev, ...updated } : prev));
      await loadList();
      refresh();
      if (typeof body.status === "string") {
        toast(
          t(body.status === "archived" ? "mail.archived" : body.status === "spam" ? "mail.spammed" : "mail.unarchived"),
        );
      }
    } catch {
      toast(t("common.error"));
    }
  };

  const patch = async (body: Record<string, unknown>) => {
    if (!thread) return;
    await patchRow(thread.id, body);
  };

  /** Deleting the open thread has to move on to another one; deleting a row
   *  from the list only has to when that row happened to be the open one. */
  const removeRow = async (gone: string) => {
    await deleteThread(gone);
    const next = (await loadList()).filter((r) => r.id !== gone);
    refresh();
    toast(t("mail.deleted"));
    if (thread?.id !== gone) return;
    setThread(null);
    navigate(next[0] ? `/email/${next[0].id}` : "/email");
  };

  const removeThread = async () => {
    if (!thread) return;
    await removeRow(thread.id);
  };

  /** Sync pulls the mailbox on screen, or every one when a combined view is. */
  const syncBox = viewMailbox(mailView) ?? "all";
  const syncLabel =
    syncBox === "all" ? t("mail.fav.allMailboxes") : (rail.mailboxes.find((m) => m.id === syncBox)?.address ?? "");

  async function doSync() {
    setSyncing(true);
    try {
      const r = await syncMail(syncBox);
      toast(syncMessage(t, r));
      await loadList();
      refreshStats();
      refresh();
    } catch {
      toast(t("email.syncFailed"));
    } finally {
      setSyncing(false);
    }
  }

  /**
   * A template seeds the body and nothing else. The subject stays the
   * conversation's own "Re: ...": swapping in the template's would fork the
   * thread in the recipient's client, which is the one thing threading headers
   * exist to prevent.
   */
  const replyTo = (target: MailThread, seed?: TemplateSeed) => {
    openComposer({
      mode: "reply",
      threadId: target.id,
      to: [],
      subject: prefixed(target.subject, "Re:"),
      body: seed?.body ?? "",
      // An answer in kristi@'s conversation leaves from kristi@.
      startFrom: ownAddress(target.mailboxId),
    });
  };

  const openReply = (seed?: TemplateSeed) => {
    if (thread) replyTo(thread, seed);
  };

  /**
   * Reply from a list row.
   *
   * The row on its own is not enough to reply from: the list payload carries
   * only the newest message and not its `toAddresses`, so `replyRecipients`
   * would answer with an empty To line - and the send confirmation exists
   * precisely so the operator can read who the reply is addressed to before it
   * goes. So the entry opens the conversation and the composer follows once the
   * full thread has landed, which is also what "reply to that one" means.
   */
  const replyFromRow = (row: MailThread) => {
    if (thread?.id === row.id) {
      replyTo(thread);
      return;
    }
    setReplyWhenLoaded(row.id);
    openThread(row.id);
  };

  useEffect(() => {
    if (!replyWhenLoaded) return;
    if (thread?.id === replyWhenLoaded) {
      replyTo(thread);
      setReplyWhenLoaded(null);
    } else if (id && id !== replyWhenLoaded) {
      // The operator moved on to another conversation while this one was
      // loading; the reply they asked for no longer belongs to anything.
      setReplyWhenLoaded(null);
    }
    // `replyTo` is recreated every render and only ever sets state, so it is
    // deliberately not a dependency - listing it would run this on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread, id, replyWhenLoaded]);

  const openForward = () => {
    if (!thread) return;
    const text = (thread.messages ?? [])
      .map((m) => `${m.fromName ?? m.fromAddress}: ${m.bodyText ?? m.snippet ?? ""}`)
      .join("\n\n");
    openComposer({
      mode: "forward",
      to: [],
      subject: prefixed(thread.subject, "Fw:"),
      body: `\n\n${t("compose.forwardHeader")}\n${text}`,
      startFrom: ownAddress(thread.mailboxId),
    });
  };

  async function aiDraft(): Promise<string | null> {
    if (!draft?.threadId) return null;
    setAiBusy(true);
    try {
      const r = await suggestMailReply(draft.threadId);
      return r.text;
    } catch (e) {
      const status = (e as { status?: number })?.status;
      toast(t(status === 503 ? "email.aiNotConfigured" : "email.aiError"));
      return null;
    } finally {
      setAiBusy(false);
    }
  }

  /** A new letter starts from the mailbox on screen. */
  const composeNew = () =>
    openComposer({ mode: "new", to: [], subject: "", body: "", startFrom: ownAddress(viewMailbox(mailView)) });

  const campaignName = useMemo(
    () => (cid: string | null) => (cid ? campaigns.byId[cid] : undefined),
    [campaigns.byId],
  );

  return (
    // PopHost so the list's filter menu and the footer's template menu share one
    // open slot: two panels up at once in a three-pane screen is a bug report.
    <PopHost>
      {/* `is-dense` is how the compact density reaches everything written in
          `crm-` CSS. The handful of values CSS cannot reach from here - a
          Tailwind utility already on an element, and `Avatar`'s inline size -
          read the same setting through `useWorkCompact`. */}
      {/* `data-view` is the phone layout's whole switch: it says which of the
          two panes is on screen, and index.css hides the other. It is written
          at every width because the rule that reads it is inside a media query -
          one breakpoint, in CSS, rather than the same number in two places. */}
      {/* `data-pane` is the other half of the same idea, one axis over: it says
          whether the open letter sits beside the list or under it, and index.css
          rebuilds the grid from it. Written at every width for the same reason -
          the rule that reads it is inside a media query, because below 1000px
          the workspace has one column and no choice to make. */}
      <div
        className={cn("crm-work", density === "compact" && "is-dense")}
        data-view={view}
        data-pane={readingPane}
        ref={gridRef}
      >
        <RailScrim open={railSheet} onClose={() => setRail(false)} />

        <FolderRail
          ref={railRef}
          open={railSheet}
          onPicked={() => setRail(false)}
          view={mailView}
          onView={(next) => {
            // A different conversation list drops the campaign it was
            // narrowed to; a label stays, as it always has across folders.
            if (next.kind === "threads" && viewRef(next) !== viewRef(listView)) setCampaignId(null);
            openView(next);
          }}
          rail={rail}
          campaignId={campaignId}
          onCampaign={setCampaignId}
          campaigns={campaigns.rows}
          labels={labels.mail}
          tag={filters.tag}
          onTag={(tag) => setFilters((f) => ({ ...f, tag }))}
          stats={stats}
          onCompose={composeNew}
          onSync={doSync}
          syncing={syncing}
          syncLabel={syncLabel}
          onToast={toast}
          onAddMailbox={() => setBoxDialog("new")}
          onEditMailbox={setBoxDialog}
        />

        {/* The rail's own divider, in the gutter on its right edge. Same handle
            as the one between the panes, and it hides at the same breakpoint:
            below 1000px the workspace is one column and there is no edge to
            drag. */}
        <button type="button" title={t("common.resizeRail")} aria-label={t("common.resizeRail")} {...railHandleProps}>
          <i className="crm-handle-line" />
        </button>

        {/* The two panes are swapped whole rather than taught to render two
            kinds of row. A thread and an IMAP message share nothing but a
            subject line: one has a status, a matched company and a reply, the
            other is a copy of what the mail server holds this second. */}
        {drafts.kind ? (
          <DraftList
            ref={leftRef}
            kind={drafts.kind}
            rows={drafts.rows}
            loading={drafts.busy}
            onOpen={(row) => void drafts.open(row)}
            renderMenu={drafts.renderMenu}
            readingPane={readingPane}
            onReadingPane={setReadingPane}
            onRail={() => setRail(true)}
          />
        ) : mailView.kind === "sentAll" ? (
          <ImapMessageList
            ref={leftRef}
            readingPane={readingPane}
            onReadingPane={setReadingPane}
            path=""
            title={t("mail.fav.allSent")}
            rows={sentAll.rows}
            total={sentAll.total}
            loading={sentAll.listBusy || (!rail.mailboxes.length && !sentAll.rows.length)}
            reason={sentAll.listReason}
            openUid={null}
            rowKey={(r) => (r as SentAllRow).key}
            openKey={sentAll.openKey}
            onOpen={() => undefined}
            onOpenRow={(r) => sentAll.openRow(r as SentAllRow)}
            recipients
            badge={(r) => sentTag(r as SentAllRow)}
            hasMore={sentAll.hasMore}
            onShowMore={sentAll.showMore}
            onReload={sentAll.reload}
            onRail={() => setRail(true)}
          />
        ) : imapPath !== null ? (
          <ImapMessageList
            ref={leftRef}
            readingPane={readingPane}
            onReadingPane={setReadingPane}
            path={imapPath}
            title={mailView.kind === "sent" ? t("mail.box.sent") : undefined}
            rows={imap.rows}
            total={imap.total}
            // A Sent whose folder list has not come in yet is loading, not empty.
            loading={imap.listBusy || !imapSource}
            reason={mailView.kind === "sent" && rail.foldersKnown(mailView.mailbox) && !sentFolder ? "no_sent" : imap.listReason}
            openUid={imap.uid}
            recipients={mailView.kind === "sent" || imap.specialUse === "\\Sent"}
            hasMore={imap.hasMore}
            onOpen={imap.open}
            onShowMore={imap.showMore}
            onReload={imap.reload}
            onRail={() => setRail(true)}
            renderMenu={imapActions.renderMenu}
          />
        ) : (
        <ThreadList
          ref={leftRef}
          readingPane={readingPane}
          onReadingPane={setReadingPane}
          rows={rows}
          openId={thread?.id ?? null}
          loading={listBusy}
          q={q}
          onQ={setQ}
          filters={filters}
          onFilters={setFilters}
          onOpen={openThread}
          campaignName={campaignName}
          labels={labels.mail}
          onReply={replyFromRow}
          onPatch={patchRow}
          onDelete={setConfirmDel}
          onPatchMany={bulk.patchMany}
          onDeleteMany={bulk.askDeleteMany}
          mailboxTag={listView.mailbox === "all" ? threadTag : undefined}
          density={density}
          onDensity={toggleDensity}
          onRail={() => setRail(true)}
        />
        )}

        <button type="button" title={t("common.resize")} aria-label={t("common.resize")} {...handleProps}>
          <i className="crm-handle-line" />
        </button>

        <div ref={rightRef} className="crm-work-pane">
          {drafts.kind ? (
            <DraftPaneEmpty kind={drafts.kind} />
          ) : mailView.kind === "sentAll" ? (
            <ImapMessagePane
              message={sentAll.message}
              loading={sentAll.msgBusy}
              reason={sentAll.msgReason}
              picked={sentAll.open !== null}
              path={sentAll.open?.path ?? ""}
              uidValidity={sentAll.open?.uidValidity ?? ""}
              mailbox={sentAll.open?.mailbox}
              onBack={back}
            />
          ) : imapPath !== null ? (
            <ImapMessagePane
              message={imap.message}
              loading={imap.msgBusy}
              reason={imap.msgReason}
              picked={imap.uid !== null}
              path={imapPath}
              uidValidity={imap.uidValidity}
              mailbox={imapSource?.mailbox}
              onBack={back}
            />
          ) : (
            <ThreadPane
              thread={thread}
              loading={threadBusy && !thread}
              labels={labels.mail}
              campaignName={campaignName(thread?.campaignId ?? null)}
              fromAddress={ownAddress(thread?.mailboxId) || stats?.sendingAddress || stats?.inboundAddress || ""}
              onReply={openReply}
              onForward={openForward}
              onPatch={patch}
              onDelete={removeThread}
              onChanged={() => thread && loadThread(thread.id)}
              onBack={back}
            />
          )}
        </div>

        {/* Last, so it sits over the panes without needing a stacking context
            of its own. Hidden over an open letter by the same rule that hides
            the list - the reply box is already at the bottom there. */}
        <WorkFab label={t("mail.compose")} onClick={composeNew}>
          <PenLine className="size-5" />
        </WorkFab>
      </div>

      {draft ? (
        <Composer
          key={draft.key}
          draft={draft}
          onDraftSaved={drafts.changed}
          sending={send.sending}
          aiBusy={aiBusy}
          // The counts move with a closed or discarded window; a save the
          // closing window still makes reports itself when it lands.
          onClose={() => {
            setDraft(null);
            drafts.changed();
          }}
          onSend={send.requestSend}
          onAi={draft.threadId ? aiDraft : undefined}
          fromAddress={stats?.sendingAddress || stats?.inboundAddress || ""}
          siteId={hostSiteId(user)}
        />
      ) : null}

      {send.confirm}

      {confirmDel ? (
        <ConfirmModal
          title={t("email.deleteTitle")}
          body={t("email.deleteConfirm")}
          confirmLabel={t("email.delete")}
          cancelLabel={t("common.cancel")}
          onConfirm={async () => {
            // Cleared after, not before: `ConfirmModal` awaits this to keep its
            // button busy, and a dialog that vanishes before the row does reads
            // as a delete that has already finished.
            await removeRow(confirmDel.id);
            setConfirmDel(null);
          }}
          onClose={() => setConfirmDel(null)}
        />
      ) : null}

      {bulk.confirm}
      {imapActions.modals}
      {drafts.modals}

      {boxDialog ? (
        <MailboxDialog
          mailboxId={boxDialog === "new" ? null : boxDialog}
          defaults={rail.defaults}
          onToast={toast}
          onClose={() => setBoxDialog(null)}
          onDone={(removed) => {
            setBoxDialog(null);
            if (removed && viewMailbox(mailView) === removed) setMailView(START_VIEW);
            void rail.reload();
            if (removed) void loadList();
          }}
        />
      ) : null}
    </PopHost>
  );
}
